export type Language = 'en' | 'ja';

export type CreditPerson = {
  name: string;
  href?: string;
};

export type CreditEntry = {
  roles: string[];
  names: CreditPerson[];
};

export const copy = {
  en: {
    app: {
      skipToContent: 'Skip to main content',
      languageSwitchLabel: 'Language switch',
      languageEnglish: 'EN',
      languageJapanese: 'JP',
    },
    hero: {
      overline: 'Hanikah Media Presents',
      title: 'Geisha Hunter',
      subtitle: 'A Documentary Film · Coming 2026',
      watchTrailer: 'Watch Trailer',
      watchTrailerAria: 'Watch the Geisha Hunter trailer',
      scroll: 'Scroll',
    },
    about: {
      heading: 'Synopsis',
      title: 'Geisha Hunter',
      quote: 'Will Taroh ever find that taste again - or something even better?',
      paragraphs: [
        'Taroh Suzuki is a legendary, award-winning coffee farmer and ultra-luxury coffee shop chain owner in Japan, known as The Geisha Hunter.',
        'After being mentored by Yoshitomo Tokugawa, the great-grandson of Tokugawa Yoshinobu, the last shogun of Japan, Taroh developed a philosophy rooted in discipline, heritage, and uncompromising standards.',
        'In 2003, Taroh and his mentor created Shogun Coffee together. The coffee quickly gained recognition, and in 2005 Taroh expanded to Tokyo, marking a major turning point in his career.',
        'In 2007, Taroh tasted a sample of Geisha coffee from Jaramillo Farm at Hacienda La Esmeralda, Mario Lot, while in the United States. He fell in love. That cup became his lifelong reference - a memory of balance, clarity, and depth that nothing else could match.',
        'In 2009, Taroh went to Panama to buy Geisha coffee at the Esmeralda special auction.',
        'In 2010, he purchased the Best of Panama coffee and has returned year after year, chasing that original cup.',
        'In 2025, he encountered a Geisha that was even better at the Best of Panama - priced at over $30,000 per kilogram. With a minimum purchase of 20 kilograms, the total cost exceeded $600,000 USD.',
        'He was outbid. The loss was devastating for Taroh.',
        "This was the second time in 14 years that Taroh didn't get the #1 coffee at the Best of Panama.",
        'Soon after, Saza Coffee achieved a historic double win, taking both the Japan Brewers Cup and the Japanese Barista Championship.',
        "Geisha Hunter follows Taroh and his team's pursuit - not just of a taste, but of perfection itself.",
      ],
    },
    subTrailer: {
      title: 'The Taroh Method',
      description: 'A look into the craft behind the cup.',
      playAria: 'Play The Taroh Method video',
      footerLabel: 'The Taroh Method',
      clickToPlay: 'Click to play',
      hoverToPreview: 'Hover to preview',
    },
    credits: {
      sectionTitle: 'Credits',
      hanikahAria: 'Hanikah Media',
      hanikahInstagramAria: 'Hanikah Media on Instagram',
      hanikahInstagramHandle: '@hanikahmedia',
      entries: [
        { roles: ['Directed and Produced By'], names: [{ name: 'Michael Rivkin' }] },
        {
          roles: ['Edited By'],
          names: [{ name: 'Kouhei Hashimoto' }, { name: 'Michael Rivkin' }],
        },
        {
          roles: ['Cinematography Team'],
          names: [
            { name: 'Michael Rivkin' },
            { name: 'Kouhei Hashimoto' },
            { name: 'Kent Ichikawa' },
          ],
        },
        { roles: ['Production Assistant'], names: [{ name: 'Justice Edwards' }] },
        {
          roles: ['Music'],
          names: [
            {
              name: 'Ayaka Arai - Double Bass',
              href: 'https://www.instagram.com/ayaka_musicbox/',
            },
          ],
        },
      ] as CreditEntry[],
    },
    footer: {
      socialNavAria: 'Social media links',
      socialLabels: {
        instagram: 'Instagram',
        youtube: 'YouTube',
        facebook: 'Facebook',
        x: 'X / Twitter',
      },
      appStoreTitle: 'App & Online Store',
      officialApp: 'Official App',
      onlineShop: 'Online Shop',
      hanikahAria: 'Hanikah Media',
      hanikahInstagramAria: 'Hanikah Media on Instagram',
      hanikahInstagramHandle: '@hanikahmedia',
      rightsReserved: 'Hanikah Media. All rights reserved.',
    },
    videoModal: {
      dialogLabel: 'Video player',
      closeAria: 'Close video player',
      closeText: 'Close',
    },
  },
  ja: {
    app: {
      skipToContent: 'メインコンテンツへスキップ',
      languageSwitchLabel: '言語切り替え',
      languageEnglish: 'EN',
      languageJapanese: 'JP',
    },
    hero: {
      overline: 'Hanikah Media 提供',
      title: 'Geisha Hunter',
      subtitle: 'ドキュメンタリー映画 · 2026年公開',
      watchTrailer: '予告編を見る',
      watchTrailerAria: 'ゲイシャ・ハンターの予告編を見る',
      scroll: 'スクロール',
    },
    about: {
      heading: 'ストーリー',
      title: 'ゲイシャ・ハンター ',
      quote: '太郎は、あの味を再び見つけられるのか――それとも、その先へ辿り着けるのか。',
      paragraphs: [
        '鈴木太郎は、受賞歴を持つ伝説的なコーヒー農家であり、日本で超高級コーヒーショップ・チェーンを展開する経営者である。彼は“ゲイシャ・ハンター”として知られている。',
        '徳川慶喜の系譜を継ぐ徳川慶朝の薫陶を受けた太郎は、規律、継承、そして妥協なき基準に根ざした哲学を築き上げた。',
        '2003年、太郎は師とともに将軍コーヒーを設立。コーヒーは瞬く間に評価を得て、2005年には東京進出を果たし、彼のキャリアにおける大きな転機となった。',
        '2007年、太郎はアメリカ滞在中に、ハシエンダ・ラ・エスメラルダのハラミージョ農園マリオ・ロットのゲイシャコーヒーを試飲する。彼はその一杯に心を奪われた。その味は、バランス、透明感、そして奥行きを備えた、他の何ものにも代えがたい“記憶の基準”となった。',
'2009年、太郎はパナマを訪れ、エスメラルダの特別オークションでゲイシャコーヒーを購入。',
'2010年にはベスト・オブ・パナマのコーヒーを落札し、それ以降も毎年現地へ足を運び、あの最初の一杯を追い求め続けている。',
'2025年、ベスト・オブ・パナマで、1kgあたり3万ドルを超える、かつてない品質のゲイシャに出会う。最低購入量は20kg――総額は60万ドルを超えた。',
'しかし彼は落札できなかった。その敗北は、太郎にとって大きな衝撃となる。',
'それは過去14年間で、太郎がベスト・オブ・パナマの1位のコーヒーを手にできなかった、わずか2度目の出来事だった。',
'その直後、サザコーヒーはジャパンブリュワーズカップとジャパンバリスタチャンピオンシップの両方を制するという歴史的快挙を成し遂げる。',
'『ゲイシャ・ハンター』は、太郎と彼のチームが追い求めるもの――それは単なる味ではなく、“完璧”という概念そのものを追い求める、終わりなき探求である。'
      ],
    },
    subTrailer: {
      title: 'タロウ・メソッド',
      description: '一粒の種から一杯の儀式へ。',
      playAria: 'タロウ・メソッド映像を再生',
      footerLabel: 'タロウ・メソッド',
      clickToPlay: 'クリックして再生',
      hoverToPreview: 'ホバーでプレビュー',
    },
    credits: {
      sectionTitle: 'クレジット',
      hanikahAria: 'Hanikah Media',
      hanikahInstagramAria: 'Hanikah MediaのInstagram',
      hanikahInstagramHandle: '@hanikahmedia',
      entries: [
        { roles: ['監督・プロデュース'], names: [{ name: 'Michael Rivkin' }] },
        {
          roles: ['編集'],
          names: [{ name: 'Kouhei Hashimoto' }, { name: 'Michael Rivkin' }],
        },
        {
          roles: ['撮影チーム'],
          names: [
            { name: 'Michael Rivkin' },
            { name: 'Kouhei Hashimoto' },
            { name: 'Kent Ichikawa' },
          ],
        },
        { roles: ['制作アシスタント'], names: [{ name: 'Justice Edwards' }] },
        {
          roles: ['音楽'],
          names: [
            {
              name: 'Ayaka Arai - コントラバス',
              href: 'https://www.instagram.com/ayaka_musicbox/',
            },
          ],
        },
      ] as CreditEntry[],
    },
    footer: {
      socialNavAria: 'SNSリンク',
      socialLabels: {
        instagram: 'Instagram',
        youtube: 'YouTube',
        facebook: 'Facebook',
        x: 'X / Twitter',
      },
      appStoreTitle: 'アプリ・オンラインストア',
      officialApp: '公式アプリ',
      onlineShop: 'オンラインショップ',
      hanikahAria: 'Hanikah Media',
      hanikahInstagramAria: 'Hanikah MediaのInstagram',
      hanikahInstagramHandle: '@hanikahmedia',
      rightsReserved: 'Hanikah Media. 無断転載を禁じます。',
    },
    videoModal: {
      dialogLabel: '動画プレーヤー',
      closeAria: '動画プレーヤーを閉じる',
      closeText: '閉じる',
    },
  },
};
